import { computed, markRaw } from 'vue'
import { useUserStore } from '@/stores/userStore'
import { t } from '@/locales'
import type {
  IContentSideMenuSection,
  IContentSideMenuItem,
} from '@/components/appLayout/ContentSideMenu.vue'
import IconLucideSettings from '~icons/lucide/settings'
import IconLucideUsers from '~icons/lucide/users'
import IconLucideWrench from '~icons/lucide/wrench'
import IconLucideFlaskConical from '~icons/lucide/flask-conical'
import IconLucideClock from '~icons/lucide/clock'
import IconLucideCalendarOff from '~icons/lucide/calendar-off'
import IconLucideMapPin from '~icons/lucide/map-pin'
import IconLucideBuilding2 from '~icons/lucide/building-2'
import IconLucidePercent from '~icons/lucide/percent'
import IconLucideTags from '~icons/lucide/tags'
import IconLucideLanguages from '~icons/lucide/languages'
import IconLucideFileText from '~icons/lucide/file-text'
import IconLucidePalette from '~icons/lucide/palette'
import IconLucidePlug from '~icons/lucide/plug'
import IconLucideSwatchBook from '~icons/lucide/swatch-book'
import IconLucidePackagePlus from '~icons/lucide/package-plus'
import IconLucideMonitor from '~icons/lucide/monitor'
import IconLucideLibrary from '~icons/lucide/library'
import IconLucidePaperclip from '~icons/lucide/paperclip'

export function useSettingsMenuSections() {
  const userStore = useUserStore()

  return computed<IContentSideMenuSection[]>(() => {
    const labItems: IContentSideMenuItem[] = []

    if (userStore.canUser('settingsGeneral.view')) {
      labItems.push({
        label: t('area.settings.tab.general'),
        to: { name: 'SettingsSystemGeneral' },
        routeName: 'SettingsSystemGeneral',
        icon: markRaw(IconLucideSettings),
      })
    }

    if (userStore.canUser('settingsBranding.manage')) {
      labItems.push({
        label: t('W: Branding'),
        to: { name: 'SettingsSystemBranding' },
        routeName: 'SettingsSystemBranding',
        icon: markRaw(IconLucideSwatchBook),
      })
    }

    if (userStore.canUser('employees.view')) {
      labItems.push({
        label: t('main-menu.item.employees'),
        to: { name: 'Employees' },
        routeName: 'Employees',
        icon: markRaw(IconLucideUsers),
      })
    }

    if (userStore.canUser('setup.view')) {
      labItems.push({
        label: t('W: Lab Lokationer'),
        to: { name: 'LabLocations' },
        routeName: 'LabLocations',
        icon: markRaw(IconLucideBuilding2),
      })
      labItems.push({
        label: t('area.drop-point.list.title'),
        to: { name: 'SettingsLabDropPoints' },
        routeName: 'SettingsLabDropPoints',
        icon: markRaw(IconLucideMapPin),
      })
      labItems.push({
        label: t('W: Lukkedage'),
        to: { name: 'SettingsClosedDays' },
        routeName: 'SettingsClosedDays',
        icon: markRaw(IconLucideCalendarOff),
      })
    }

    if (userStore.canUser('equipment.view')) {
      labItems.push({
        label: t('main-menu.item.lab-equipment'),
        to: { name: 'LabEquipment' },
        routeName: 'LabEquipment',
        icon: markRaw(IconLucideWrench),
      })
    }

    if (userStore.canUser('integrations.manage')) {
      labItems.push({
        label: t('area.settings.section.integrations'),
        to: { name: 'SettingsIntegrations' },
        routeName: 'SettingsIntegrations',
        icon: markRaw(IconLucidePlug),
      })
    }

    const setupItems: IContentSideMenuItem[] = []

    if (userStore.canUser('reportTemplates.view')) {
      setupItems.push({
        label: t('W: Rapporter'),
        to: { name: 'SettingsReports' },
        routeName: 'SettingsReports',
        icon: markRaw(IconLucideFileText),
      })
    }

    if (userStore.canUser('setup.view')) {
      setupItems.push({
        label: t('area.analysis-setup.tab.tats'),
        to: { name: 'SettingsTats' },
        routeName: 'SettingsTats',
        icon: markRaw(IconLucideClock),
      })
      setupItems.push({
        label: t('W: Analyseopsætning'),
        to: { name: 'SampleAreas' },
        routeName: 'SampleAreas',
        icon: markRaw(IconLucideFlaskConical),
      })
      setupItems.push({
        label: t('W: Tillægsydelser'),
        to: { name: 'AddonServices' },
        routeName: 'AddonServices',
        icon: markRaw(IconLucidePackagePlus),
      })
      setupItems.push({
        label: t('main-menu.item.pricing-segments'),
        to: { name: 'PricingSegments' },
        routeName: 'PricingSegments',
        icon: markRaw(IconLucidePercent),
      })
      setupItems.push({
        label: t('W: Prioritetsfarver'),
        to: { name: 'SettingsPriorityColors' },
        routeName: 'SettingsPriorityColors',
        icon: markRaw(IconLucidePalette),
      })
    }

    if (userStore.canUser('displays.manage')) {
      setupItems.push({
        label: t('W: Skærme'),
        to: { name: 'SettingsDisplays' },
        routeName: 'SettingsDisplays',
        icon: markRaw(IconLucideMonitor),
      })
    }

    if (userStore.canUser('translations.view')) {
      setupItems.push({
        label: t('main-menu.item.translations'),
        to: { name: 'LabTranslations' },
        routeName: 'LabTranslations',
        icon: markRaw(IconLucideLanguages),
      })
    }

    const libraryItems: IContentSideMenuItem[] = []

    if (userStore.canUser('collections.view')) {
      libraryItems.push({
        label: t('W: Lister og beskrivelser'),
        to: { name: 'SettingsReportDescriptions' },
        routeName: 'SettingsReportDescriptions',
        icon: markRaw(IconLucideLibrary),
      })
    }

    if (userStore.canUser('reportTemplates.view')) {
      libraryItems.push({
        label: t('W: Bilag'),
        to: { name: 'SettingsReportAttachments' },
        routeName: 'SettingsReportAttachments',
        icon: markRaw(IconLucidePaperclip),
      })
    }

    if (userStore.canUser('setup.view')) {
      libraryItems.push({
        label: t('W: Labels'),
        to: { name: 'LabelsLibrary' },
        routeName: 'LabelsLibrary',
        icon: markRaw(IconLucideTags),
      })
    }

    // ContentSideMenu renders a section's header regardless of whether it has
    // any items — a user with none of a section's permissions would otherwise
    // see an empty heading (permission-visibility.md: hide, don't disable).
    return [
      { title: t('W: Lab'), items: labItems },
      { title: t('W: Opsætning'), items: setupItems },
      { title: t('W: Bibliotek'), items: libraryItems },
    ].filter((section) => section.items.length > 0)
  })
}
